import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../lib/project-context';
import { projectsAPI, scenariosAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FolderKanban, ArrowRight, Plus, Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/* ════════════════════════════════════════════
   RequiresProject — guided project selection or creation
   ════════════════════════════════════════════ */
export function RequiresProject({ children, moduleName }) {
  const { projects, activeProject, setActiveProject, loadingProjects, refreshProjects } = useProject();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  if (loadingProjects) return null;

  if (!activeProject) {
    const hasProjects = projects.length > 0;

    const handleCreate = async () => {
      if (!name.trim()) return;
      setCreating(true);
      try {
        const res = await projectsAPI.create({ name, status: 'draft' });
        setActiveProject(res.data);
        refreshProjects();
        toast.success(`Proyecto "${res.data.name}" creado`);
        navigate(`/projects/${res.data.id}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al crear');
      }
      setCreating(false);
    };

    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <Card className="max-w-lg w-full rounded-xl border-border/50 shadow-sm" data-testid="no-project-state">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(15,157,170,0.08)' }}>
                <FolderKanban className="h-6 w-6" style={{ color: 'var(--module-primary)' }} />
              </div>
              <h2 className="font-display text-lg font-semibold mb-1">
                {hasProjects ? 'Selecciona un proyecto' : 'Crea tu primer proyecto'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasProjects
                  ? `Para usar ${moduleName}, selecciona uno de tus proyectos.`
                  : `Necesitas un proyecto para acceder a ${moduleName}.`}
              </p>
            </div>

            {/* Quick-select existing projects */}
            {hasProjects && (
              <div className="space-y-2 mb-5" data-testid="project-quick-select">
                {projects.filter(p => !p.deleted_at).slice(0, 5).map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setActiveProject(p); toast.success(`Proyecto: ${p.name}`); }}
                    className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg border border-border/60 hover:bg-accent/40 hover:border-border transition-colors"
                    data-testid={`quick-select-project-${p.id}`}
                  >
                    <FolderKanban className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      {p.location && <p className="text-[11px] text-muted-foreground">{p.location}</p>}
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                  </button>
                ))}
              </div>
            )}

            {/* Inline create */}
            {showCreate ? (
              <div className="space-y-3 pt-2 border-t border-border/50" data-testid="inline-create-project">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre del proyecto</Label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Mi proyecto inmobiliario"
                    autoFocus
                    data-testid="inline-project-name"
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowCreate(false)}>Cancelar</Button>
                  <Button
                    size="sm"
                    className="flex-1 text-white"
                    style={{ background: 'var(--accent-primary)' }}
                    onClick={handleCreate}
                    disabled={!name.trim() || creating}
                    data-testid="inline-create-project-btn"
                  >
                    {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                    Crear
                  </Button>
                </div>
              </div>
            ) : (
              <div className={hasProjects ? 'pt-3 border-t border-border/50' : ''}>
                <Button
                  variant={hasProjects ? 'outline' : 'default'}
                  className={`w-full ${hasProjects ? '' : 'text-white'}`}
                  style={hasProjects ? {} : { background: 'var(--accent-primary)' }}
                  onClick={() => setShowCreate(true)}
                  data-testid="show-create-project"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {hasProjects ? 'O crear un nuevo proyecto' : 'Crear proyecto'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
}

/* ════════════════════════════════════════════
   RequiresScenario — guided scenario selection or creation
   ════════════════════════════════════════════ */
export function RequiresScenario({ children, moduleName }) {
  const { activeProject, activeScenario, scenarios, setActiveScenario, loadingScenarios, refreshScenarios } = useProject();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  if (!activeProject) {
    return <RequiresProject moduleName={moduleName}>{children}</RequiresProject>;
  }

  if (loadingScenarios) return null;

  if (!activeScenario) {
    const hasScenarios = scenarios.length > 0;

    const handleCreate = async () => {
      if (!name.trim()) return;
      setCreating(true);
      try {
        const res = await scenariosAPI.create(activeProject.id, { name });
        setActiveScenario(res.data);
        refreshScenarios();
        toast.success(`Escenario "${res.data.name}" creado`);
        navigate(`/analyzer/wizard/${activeProject.id}/${res.data.id}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al crear');
      }
      setCreating(false);
    };

    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <Card className="max-w-lg w-full rounded-xl border-border/50 shadow-sm" data-testid="no-scenario-state">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(15,157,170,0.08)' }}>
                <Layers className="h-6 w-6" style={{ color: 'var(--module-primary)' }} />
              </div>
              <h2 className="font-display text-lg font-semibold mb-1">
                {hasScenarios ? 'Selecciona un escenario' : 'Crea tu primer escenario'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasScenarios
                  ? `Selecciona un escenario de "${activeProject.name}" para usar ${moduleName}.`
                  : `Crea un escenario en "${activeProject.name}" para empezar a analizar.`}
              </p>
            </div>

            {/* Quick-select existing scenarios */}
            {hasScenarios && (
              <div className="space-y-2 mb-5" data-testid="scenario-quick-select">
                {scenarios.slice(0, 5).map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveScenario(s); toast.success(`Escenario: ${s.name}`); }}
                    className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-lg border border-border/60 hover:bg-accent/40 hover:border-border transition-colors"
                    data-testid={`quick-select-scenario-${s.id}`}
                  >
                    <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      {s.description && <p className="text-[11px] text-muted-foreground truncate">{s.description}</p>}
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                  </button>
                ))}
              </div>
            )}

            {/* Inline create */}
            {showCreate ? (
              <div className="space-y-3 pt-2 border-t border-border/50" data-testid="inline-create-scenario">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre del escenario</Label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Caso Base, Optimista, Pesimista..."
                    autoFocus
                    data-testid="inline-scenario-name"
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowCreate(false)}>Cancelar</Button>
                  <Button
                    size="sm"
                    className="flex-1 text-white"
                    style={{ background: 'var(--accent-primary)' }}
                    onClick={handleCreate}
                    disabled={!name.trim() || creating}
                    data-testid="inline-create-scenario-btn"
                  >
                    {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                    Crear y configurar
                  </Button>
                </div>
              </div>
            ) : (
              <div className={hasScenarios ? 'pt-3 border-t border-border/50' : ''}>
                <Button
                  variant={hasScenarios ? 'outline' : 'default'}
                  className={`w-full ${hasScenarios ? '' : 'text-white'}`}
                  style={hasScenarios ? {} : { background: 'var(--accent-primary)' }}
                  onClick={() => setShowCreate(true)}
                  data-testid="show-create-scenario"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {hasScenarios ? 'O crear un nuevo escenario' : 'Crear escenario'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
}
